import type { CollectionKey } from '../CollectionRegistry/CollectionKey.interface'
import type { CollectionRequest } from '../CollectionRegistry/CollectionRequest.interface'

const KEY_DELIMITER = '::'

const normalise = (value?: string) => value?.trim().toLowerCase() ?? ''

export function serializeDescriptor(descriptor: CollectionKey): string {
  return [descriptor.table, descriptor.select, descriptor.where, descriptor.params].map(normalise).join(KEY_DELIMITER)
}

export const sanitizeForId = (input: string) =>
  input
    .replace(/[^\w-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

function toSnakeCase(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase()
}

const quoteIdentifier = (value: string) => `"${value.replace(/"/g, '""')}"`

const quoteString = (value: string) => `'${value.replace(/'/g, "''")}'`

export function buildWhereClause(where?: Record<string, unknown>) {
  if (!where) return undefined

  const parts: string[] = []

  for (const [rawKey, rawValue] of Object.entries(where)) {
    if (rawValue === undefined || rawValue === null) continue
    const column = quoteIdentifier(toSnakeCase(rawKey))

    if (Array.isArray(rawValue)) {
      if (rawValue.length === 0) {
        parts.push('1=0')
        continue
      }
      const values = rawValue
        .filter((value) => value !== undefined && value !== null)
        .map((value) => quoteString(String(value)))
      if (values.length === 0) {
        parts.push('1=0')
        continue
      }
      parts.push(`${column} IN (${values.join(',')})`)
      continue
    }

    switch (typeof rawValue) {
      case 'string':
      case 'number':
      case 'bigint':
      case 'boolean':
        parts.push(`${column} = ${quoteString(String(rawValue))}`)
        break
      default:
        break
    }
  }

  if (parts.length === 0) return undefined
  return parts.join(' AND ')
}

function stableStringify(value: unknown): string {
  if (value === undefined) return 'undefined'
  if (typeof value === 'function') return JSON.stringify(value.toString())
  if (value === null || typeof value !== 'object') return JSON.stringify(value)

  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`

  const entries = Object.entries(value as Record<string, unknown>)
    .map(([key, val]) => [key, stableStringify(val)] as const)
    .sort(([a], [b]) => a.localeCompare(b))

  return `{${entries.map(([key, val]) => `${JSON.stringify(key)}:${val}`).join(',')}}`
}

export function descriptorFromRequest(request: CollectionRequest): CollectionKey {
  return {
    table: request.table,
    where: request.where ? stableStringify(request.where) : undefined,
  }
}
